import {
  ChangeCurrentCartContactFailedAction,
  ChangeCurrentCartContactSucceededAction,
  ChangeCurrentCartEmailFailedAction,
  ChangeCurrentCartEmailSucceededAction,
  RegisterCustomerAndChangeAddressesAndSignInRequestedAction,
  RegisterCustomerAndChangeAddressesAndSignInRequestedPayload,
  changeCurrentCartContactFailedType,
  changeCurrentCartContactRequested,
  changeCurrentCartContactSucceededType,
  changeCurrentCartEmailFailedType,
  changeCurrentCartEmailRequested,
  changeCurrentCartEmailSucceededType,
  registerCustomerAndChangeAddressesAndSignInFailed,
  registerCustomerAndChangeAddressesAndSignInSucceeded
} from '../../carts/cartsSlice';
import {
  RegisterCustomerFailedAction,
  RegisterCustomerSucceededAction,
  SignInCustomerFailedAction,
  SignInCustomerSucceededAction,
  registerCustomerFailedType,
  registerCustomerRequested,
  registerCustomerSucceededType,
  signInCustomerFailedType,
  signInCustomerRequested,
  signInCustomerSucceededType
} from '@spa/lib/store/auth/authSlice';
import { call, put } from 'typed-redux-saga';
import { RegisterFailedCause } from '@spa/lib/store/auth/RegisterFailedCause';
import generateActionId from '../../../generateActionId';
import waitForSucceededOrFailedAction from '@spa/lib/store/sagas/session/waitForSucceededOrFailedAction';

function* registerCustomerAndGetFailedCause(
  registerValues: RegisterCustomerAndChangeAddressesAndSignInRequestedPayload['register']
): Generator<unknown, RegisterFailedCause | null> {
  const registerCustomerRequestedActionId: number = generateActionId();
  yield* put(
    registerCustomerRequested({
      actionId: registerCustomerRequestedActionId,
      ...registerValues
    })
  );

  const waitResult = yield* waitForSucceededOrFailedAction<
    typeof registerCustomerSucceededType,
    typeof registerCustomerFailedType,
    RegisterCustomerSucceededAction,
    RegisterCustomerFailedAction
  >(registerCustomerRequestedActionId, registerCustomerSucceededType, registerCustomerFailedType);
  return waitResult.type === 'failed' ? waitResult.action.payload.cause : null;
}

function* changeEmailAndVerifyIfFailed(
  emailPayload: RegisterCustomerAndChangeAddressesAndSignInRequestedPayload['emails'][number]
): Generator<unknown, boolean> {
  const actionId: number = generateActionId();
  yield* put(
    changeCurrentCartEmailRequested({
      ...emailPayload,
      actionId
    })
  );

  const waitResult = yield* waitForSucceededOrFailedAction<
    typeof changeCurrentCartEmailSucceededType,
    typeof changeCurrentCartEmailFailedType,
    ChangeCurrentCartEmailSucceededAction,
    ChangeCurrentCartEmailFailedAction
  >(actionId, changeCurrentCartEmailSucceededType, changeCurrentCartEmailFailedType);
  return waitResult.type === 'failed';
}

function* changeContactAndVerifyIfFailed(
  fields: RegisterCustomerAndChangeAddressesAndSignInRequestedPayload['contacts'][number]
): Generator<unknown, boolean> {
  const actionId: number = generateActionId();
  yield* put(
    changeCurrentCartContactRequested({
      ...fields,
      actionId
    })
  );

  const waitResult = yield* waitForSucceededOrFailedAction<
    typeof changeCurrentCartContactSucceededType,
    typeof changeCurrentCartContactFailedType,
    ChangeCurrentCartContactSucceededAction,
    ChangeCurrentCartContactFailedAction
  >(actionId, changeCurrentCartContactSucceededType, changeCurrentCartContactFailedType);
  return waitResult.type === 'failed';
}

function* signInCustomerFailed({
  email,
  password
}: RegisterCustomerAndChangeAddressesAndSignInRequestedPayload['register']): Generator<
  unknown,
  boolean
> {
  const actionId: number = generateActionId();
  yield* put(
    signInCustomerRequested({
      actionId,
      email,
      password
    })
  );

  const waitResult = yield* waitForSucceededOrFailedAction<
    typeof signInCustomerSucceededType,
    typeof signInCustomerFailedType,
    SignInCustomerSucceededAction,
    SignInCustomerFailedAction
  >(actionId, signInCustomerSucceededType, signInCustomerFailedType);
  return waitResult.type === 'failed';
}

function* changeEmailsAndVerifyIfAnyFailed(
  emails: RegisterCustomerAndChangeAddressesAndSignInRequestedAction['payload']['emails']
): Generator<unknown, boolean> {
  let anyFailed = false;
  if (!anyFailed) {
    for (const email of emails) {
      anyFailed = (yield* call(changeEmailAndVerifyIfFailed, email)) as unknown as boolean;
      if (anyFailed) {
        break;
      }
    }
  }
  return anyFailed;
}

function* changeContactsAndVerifyIfAnyFailed(
  contacts: RegisterCustomerAndChangeAddressesAndSignInRequestedAction['payload']['contacts']
): Generator<unknown, boolean> {
  let anyFailed = false;
  if (!anyFailed) {
    for (const contact of contacts) {
      anyFailed = (yield* call(changeContactAndVerifyIfFailed, contact)) as unknown as boolean;
      if (anyFailed) {
        break;
      }
    }
  }
  return anyFailed;
}

function* registerCustomerAndChangeAddressesAndSignInSaga({
  payload: { actionId, register, contacts, emails }
}: RegisterCustomerAndChangeAddressesAndSignInRequestedAction): Generator {
  const registrationFailedCause = (yield* call(
    registerCustomerAndGetFailedCause,
    register
  )) as unknown as RegisterFailedCause | null;

  let anyCartChangeFailed = false;
  if (registrationFailedCause === null) {
    anyCartChangeFailed = (yield* call(
      changeEmailsAndVerifyIfAnyFailed,
      emails
    )) as unknown as boolean;
  }

  if (registrationFailedCause === null && !anyCartChangeFailed) {
    anyCartChangeFailed = (yield* call(
      changeContactsAndVerifyIfAnyFailed,
      contacts
    )) as unknown as boolean;
  }

  let signInFailed = false;
  if (registrationFailedCause === null && !anyCartChangeFailed) {
    signInFailed = (yield* call(signInCustomerFailed, register)) as unknown as boolean;
  }

  if (registrationFailedCause !== null || anyCartChangeFailed || signInFailed) {
    yield* put(
      registerCustomerAndChangeAddressesAndSignInFailed({
        requestedActionId: actionId,
        registerFailedCause: registrationFailedCause,
        cartChangesFailed: anyCartChangeFailed,
        signInFailed
      })
    );
  } else {
    yield* put(
      registerCustomerAndChangeAddressesAndSignInSucceeded({
        requestedActionId: actionId
      })
    );
  }
}

export default registerCustomerAndChangeAddressesAndSignInSaga;
